import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMG, GALLERY } from "@/lib/images";
import { Reveal, SectionLabel, ArchImage, DietBadge, useSettings } from "@/components/public/shared";
import Founders from "@/components/public/Founders";
import { useEnquiry } from "@/components/public/EnquiryProvider";

const SERVICES = [
  { title: "Event & Celebration Catering", desc: "Weddings, anniversaries, birthdays, kitty parties, receptions and private celebrations — crafted around every occasion.", img: IMG.indianWeddingBuffet, cta: "Plan Your Event", type: "event", to: "/catering" },
  { title: "Corporate & Institutional Catering", desc: "Offices, conferences, meetings, product launches, employee meals and institutional requirements at any scale.", img: IMG.corporate, cta: "Discuss Your Requirement", type: "bulk", to: "/food-services" },
  { title: "Daily Food Services", desc: "Recurring meal programs for offices, clubs, malls, institutions and managed properties — breakfast to dinner.", img: IMG.indianFestive, cta: "Explore Food Services", type: "bulk", to: "/food-services" },
  { title: "Hospitality & F&B Operations", desc: "Kitchen operations, menu planning, service management, food production and operational consulting.", img: IMG.chefKitchen, cta: "Explore Hospitality Services", type: null, to: "/hospitality" },
];

const ARCH_NAV = [
  { label: "Our Menu", to: "/menu", img: IMG.momoGreen },
  { label: "Menu Packages", to: "/packages", img: IMG.indianAppetizers },
  { label: "Event Catering", to: "/catering", img: IMG.indianWeddingBuffet },
  { label: "Bulk & Food Services", to: "/food-services", img: "https://customer-assets-jt897jd0.emergentagent.net/job_ethical-dining-group/artifacts/uvlmsjbr_de6925b6-bc94-4d1a-baca-be1502d2f410.png" },
  { label: "Events", to: "/events", img: IMG.ballroom },
  { label: "Gallery", to: "/gallery", img: IMG.chefPlating },
];

export default function Home() {
  const { open } = useEnquiry();
  const s = useSettings();
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/gallery").then(({ data }) => setFeatured((data || []).filter((g) => g.is_featured))).catch(() => setFeatured([]));
  }, []);
  const galleryItems = featured.length
    ? featured.map((g) => ({ url: g.url, label: g.label, media_type: g.media_type }))
    : GALLERY.slice(0, 8).map((g) => ({ ...g, media_type: "image" }));

  return (
    <div>
      {/* 1. Cinematic Hero */}
      <section className="relative flex min-h-screen items-center overflow-hidden">
        <div className="absolute inset-0">
          {s.hero_video_url ? (
            <video autoPlay muted loop playsInline preload="metadata" poster={IMG.heroDimSum} className="h-full w-full object-cover">
              <source src={s.hero_video_url} type="video/mp4" />
            </video>
          ) : (
            <img src={IMG.heroDimSum} alt="Mee2Momos gourmet pure veg dim sum" className="h-full w-full object-cover animate-ken-burns" />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/60 to-charcoal/30" />
          <div className="absolute inset-0 grain opacity-[0.05]" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-4 pt-24 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fade-up">
            <div className="mb-6"><DietBadge /></div>
            <h1 className="font-display text-4xl font-light leading-[1.02] text-white sm:text-6xl lg:text-7xl">
              Crafting Experiences.<br /><span className="italic text-gold">Serving Excellence.</span>
            </h1>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
              Premium pure vegetarian &amp; Jain-Friendly catering, food services and hospitality experiences for celebrations, businesses and exceptional occasions.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link to="/catering" data-testid="hero-cta-explore" className="group inline-flex items-center justify-center gap-2 bg-gold px-8 py-4 text-[12px] font-semibold uppercase tracking-editorial text-charcoal transition-colors hover:bg-white">
                Explore Our Services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <button data-testid="hero-cta-enquire" onClick={() => open("event")} className="inline-flex items-center justify-center gap-2 border border-white/40 px-8 py-4 text-[12px] font-semibold uppercase tracking-editorial text-white transition-colors hover:border-gold hover:text-gold">
                Enquire Now
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-[10px] uppercase tracking-editorial text-white/50 lg:block">Scroll to discover</div>
      </section>

      {/* Arch-card navigation (Speciality-style) */}
      <section className="relative overflow-hidden bg-espresso py-16 sm:py-20">
        <div className="absolute inset-0 grain opacity-[0.05]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-10 text-center">
            <span className="text-[11px] font-semibold uppercase tracking-editorial text-gold">Welcome to Mee2Momos</span>
            <h2 className="mt-3 font-display text-3xl font-light text-ivory sm:text-4xl">Explore our world of hospitality</h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-6">
            {ARCH_NAV.map((a, i) => (
              <Reveal key={a.label} delay={i * 70}>
                <Link to={a.to} data-testid={`archnav-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="group block">
                  <div className="overflow-hidden arch-full border border-gold/25 aspect-[3/4]">
                    <img src={a.img} alt={a.label} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                  </div>
                  <div className="mt-3 flex items-center justify-between border-t border-gold/30 pt-3">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-ivory group-hover:text-gold">{a.label}</span>
                    <ArrowUpRight className="h-4 w-4 text-gold transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 2. Hospitality Introduction */}
      <section className="bg-ivory py-20 sm:py-28 lg:py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8">
          <Reveal className="lg:col-span-5">
            <ArchImage src={IMG.chefPlating} alt="Chef plating a gourmet dish" ratio="aspect-[4/5]" />
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <SectionLabel>Hospitality, thoughtfully crafted</SectionLabel>
            <h2 className="mt-6 font-display text-3xl font-light leading-tight text-espresso sm:text-4xl lg:text-5xl">
              Great hospitality is more than exceptional food.
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-inkmuted">
              <p>It is the combination of culinary craft, thoughtful service, seamless execution and experiences that people remember.</p>
              <p>From intimate celebrations and corporate gatherings to daily food services and large-scale catering, we bring together food, people and operations to create hospitality experiences built around every requirement — always <span className="text-jain font-medium">pure vegetarian &amp; Jain-Friendly</span>.</p>
            </div>
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-editorial text-espresso hover:text-gold">
              Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Founders */}
      <Founders bg="bg-ivory" />

      {/* 3-7. What We Do */}
      <section className="bg-cream py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-16 text-center">
            <SectionLabel className="justify-center [&_span:last-child]:text-gold">What We Do</SectionLabel>
            <h2 className="mt-5 font-display text-4xl font-light text-espresso sm:text-5xl">Four ways we craft hospitality</h2>
          </Reveal>
          <div className="space-y-6 md:space-y-10">
            {SERVICES.map((sv, i) => (
              <Reveal key={sv.title} delay={i * 60}>
                <div className={`grid grid-cols-1 items-stretch gap-0 overflow-hidden border border-subtle bg-ivory md:grid-cols-2 ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}>
                  <div className="relative min-h-[280px] [direction:ltr]">
                    <img src={sv.img} alt={sv.title} className="absolute inset-0 h-full w-full object-cover" />
                  </div>
                  <div className="flex flex-col justify-center p-8 sm:p-12 [direction:ltr]">
                    <span className="font-display text-5xl text-gold/30">0{i + 1}</span>
                    <h3 className="mt-3 font-display text-2xl font-medium text-espresso sm:text-3xl">{sv.title}</h3>
                    <p className="mt-4 text-base leading-relaxed text-inkmuted">{sv.desc}</p>
                    <div className="mt-7">
                      {sv.type ? (
                        <button data-testid={`whatwedo-cta-${i}`} onClick={() => open(sv.type)} className="group inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-editorial text-espresso hover:text-gold">
                          {sv.cta} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </button>
                      ) : (
                        <Link to={sv.to} data-testid={`whatwedo-cta-${i}`} className="group inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-editorial text-espresso hover:text-gold">
                          {sv.cta} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Culinary Experience — signature dim sum */}
      <section className="bg-ivory py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-2xl">
            <SectionLabel>The Culinary Experience</SectionLabel>
            <h2 className="mt-5 font-display text-4xl font-light text-espresso sm:text-5xl">Signature pure-veg dim sum, reimagined</h2>
            <p className="mt-5 text-inkmuted">Artisanal, colour-crafted dumplings across Chinese, Thai, Japanese, Italian, Mexican and European cuisines — entirely pure vegetarian &amp; Jain-Friendly.</p>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
            {[
              { img: IMG.momoGreen, name: "Spinach Dim Sum" },
              { img: IMG.momoPurple, name: "Butterfly Pea Momos" },
              { img: IMG.momoJade, name: "Jade Chilli Dumplings" },
              { img: IMG.momoWhite, name: "Crystal Garden Dim Sum" },
            ].map((d, i) => (
              <Reveal key={d.name} delay={i * 80}>
                <div className="group">
                  <div className="overflow-hidden arch-top border border-gold/20 aspect-[3/4]">
                    <img src={d.img} alt={d.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  </div>
                  <p className="mt-3 text-center font-display text-lg text-espresso">{d.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/menu" data-testid="home-view-menu" className="inline-flex items-center gap-2 border border-espresso px-8 py-4 text-[12px] font-semibold uppercase tracking-editorial text-espresso transition-colors hover:bg-espresso hover:text-ivory">
              View Full Menu <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 10. Signature statement */}
      <section className="relative overflow-hidden py-28 sm:py-40">
        <div className="absolute inset-0">
          <img src={IMG.banquetArch} alt="Luxury banquet" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-charcoal/75" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <h2 className="font-display text-3xl font-light leading-tight text-ivory sm:text-5xl lg:text-6xl">
              We don't simply serve an occasion.<br /><span className="italic text-gold">We shape the experience around it.</span>
            </h2>
          </Reveal>
        </div>
      </section>

      {/* 11. Events & Experiences preview */}
      <section className="bg-cream py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <SectionLabel>Events &amp; Experiences</SectionLabel>
              <h2 className="mt-5 font-display text-4xl font-light text-espresso sm:text-5xl">Every celebration, its own story</h2>
            </div>
            <Link to="/events" className="text-[12px] font-semibold uppercase tracking-editorial text-espresso hover:text-gold">All Experiences →</Link>
          </Reveal>
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3">
            {["Weddings", "Corporate Events", "Anniversaries", "Product Launches", "Kitty Parties", "Conferences"].map((e, i) => {
              const imgs = [IMG.banquet, IMG.corporate, IMG.fineTable, IMG.corporateHall, IMG.buffet, IMG.ballroom];
              return (
                <Reveal key={e} delay={i * 50}>
                  <div className="group relative overflow-hidden aspect-[4/5]">
                    <img src={imgs[i]} alt={e} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal/85 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-5">
                      <h3 className="font-display text-xl text-ivory sm:text-2xl">{e}</h3>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 12. Gallery preview */}
      <section className="bg-ivory py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-12 text-center">
            <SectionLabel className="justify-center">Moments We Create</SectionLabel>
            <h2 className="mt-5 font-display text-4xl font-light text-espresso sm:text-5xl">A glimpse of our craft</h2>
          </Reveal>
          <div className="columns-2 gap-4 sm:columns-3 lg:columns-4 [&>*]:mb-4" data-testid="home-gallery">
            {galleryItems.slice(0, 8).map((g, i) => (
              <Reveal key={i} delay={i * 40}>
                <div className="overflow-hidden">
                  {g.media_type === "video" ? (
                    <video src={g.url} controls muted loop autoPlay playsInline preload="metadata" className="w-full object-cover" />
                  ) : (
                    <img src={g.url} alt={g.label} loading="lazy" className="w-full object-cover transition-transform duration-700 hover:scale-105" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/gallery" data-testid="home-view-gallery" className="inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-editorial text-espresso hover:text-gold">
              Explore the Gallery <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
